import React, {useContext, useMemo, useRef, useState} from 'react';
import {Form} from '@unform/web';
import Yup from '../../../infra/Yup';
import {ThemeContext} from '../../../apiContexts';
import * as dateUtil from '../../../helpers/date';
import {exportReportViaQueue, getDate, getMemorizeReportForm, memorizeReportForm, reportExportFormat, notifyReportJobMailNotice, notifyReportJobEnqueued, reportJobErrorMessage} from '../../../helpers/report';
import {useIntl} from 'react-intl';
import {useToasts} from 'react-toast-notifications';
import {ReactSelectStyled} from '../../../styles';
import DeviceGroupSelect from './deviceGroupSelect';
import FooterActions from './footerActions';
import PeriodDate from './periodDate';
import {eventReportValidation} from './validation';
import {Input as UInput} from '../../../components/input';

const schema = eventReportValidation(Yup);

const EventConfig = (props) => {
        const {
            devices,
            redirectToReport,
            groups,
            events,
            notifyToast,
            toggle,
        } = props;
        const formRef = useRef(null);
        const intl = useIntl();
        const {addToast} = useToasts();
    const theme = useContext(ThemeContext);
    const periodOptions = [
        {id: 'dateToDate', title: intl.formatMessage({id: 'reportCustomPeriod'})},
        {id: 'nowSofar', title: intl.formatMessage({id: 'reportToday'})},
        {id: 'yesterday', title: intl.formatMessage({id: 'reportYesterday'})},
        {id: 'currentWeekSoFar', title: intl.formatMessage({id: 'reportThisWeek'})},
        {id: 'lastWeek', title: intl.formatMessage({id: 'reportPreviousWeek'})},
        {id: 'currentMonthSoFar', title: intl.formatMessage({id: 'reportCurrentMonth'})},
        {id: 'lastMonth', title: intl.formatMessage({id: 'reportPreviousMonth'})},
    ];
        const initialData = useMemo(() => getMemorizeReportForm(), []);

        const [startDate, setStartDate] = useState(getDate(initialData.startDate));
        const [endDate, setEndDate] = useState(getDate(initialData.endDate));
        const [period, setPeriod] = useState(initialData.period);
        const [outer, setOuter] = useState();

        const toReport = async (data) => {
            try {
                const date = (data.period === 'dateToDate') ? dateUtil['dateToDate'](data.startDate, data.endDate) : dateUtil[data.period]();
                data.startDate = date.from;
                data.endDate = date.to;

                await schema.validate(data, {
                    abortEarly: false,
                    recursive: true,
                });

                const query = new URLSearchParams();

                query.append('_dc', new Date().getTime().toString());
                query.append('from', date.from);
                query.append('to', date.to);

                const eventTypes = (data.event || []).map((id) => (id === 'all' ? 'allEvents' : id));
                eventTypes.forEach((type) => query.append('type', type));

                if (data.device) {
                    data.device.map((id) => query.append('deviceId', id));
                }

                if (data.group) {
                    query.append('groupId', data.group);
                }

                memorizeReportForm(data);

                if (outer) {
                    const format = reportExportFormat(outer);
                    const mail = outer === 'mail';

                    // Sem loading/spinner e sem esperar terminar aqui: o pedido é assíncrono, o
                    // toast já avisa e o modal fecha assim que o pedido é aceito (onEnqueued) --
                    // mesmo padrão de routeConfig.jsx.
                    exportReportViaQueue(
                        'events', format,
                        data.device ? data.device.map(Number) : [],
                        data.group ? [Number(data.group)] : [],
                        date.from, date.to,
                        (job) => {
                            notifyReportJobEnqueued(addToast, intl, job);
                            notifyReportJobMailNotice(addToast, intl, job);
                            setOuter(null);
                            toggle();
                        },
                        mail,
                        {eventTypes},
                    ).catch((err) => {
                        addToast(reportJobErrorMessage(intl, err, 'reportEmailError'), {
                            appearance: 'error', autoDismiss: true,
                        });
                    });
                    return;
                }

                const url = `event?${query.toString()}`;
                redirectToReport(url);
            } catch (err) {
                const validationErrors = {};
                if (err instanceof Yup.ValidationError) {
                    err.inner.forEach((error) => {
                        validationErrors[error.path] = error.message;
                    });
                    formRef.current.setErrors(validationErrors);
                } else {
                    notifyToast('error', err);
                }
            }
        };

        return (
            <div>
                {devices && groups && (
                    <Form ref={formRef} schema={schema} initialData={initialData} onSubmit={toReport}>


                        <UInput hidden name="startDate" value={startDate}/>
                        <UInput hidden name="endDate" value={endDate}/>

                        <DeviceGroupSelect {...{devices, groups, initialData}} />

                        <ReactSelectStyled // events
                            radius="9px"
                            options={events}
                            name="event"
                            noOptionsMessage={() => intl.formatMessage({id: 'sharedNoResultFound'})}
                            placeholder={intl.formatMessage({id: 'reportChooseEvents'})}
                            className="w-100 mt-3"
                            multiple
                        />

                        <ReactSelectStyled // period
                            radius="9px"
                            options={periodOptions}
                            name="period"
                            noOptionsMessage={() => intl.formatMessage({id: 'sharedNoResultFound'})}
                            placeholder={intl.formatMessage({id: 'reportChoosePeriod'})}
                            className="w-100 mt-3"
                            onChange={(_data) => {
                                if (_data) {
                                    setPeriod(_data.id);
                                } else {
                                    setPeriod(initialData.period);
                                }
                            }}
                        />

                        {period === 'dateToDate' && (
                            <PeriodDate {...{setStartDate, startDate, setEndDate, endDate}} />
                        )}

                        <FooterActions theme={theme} setOuter={setOuter}/>
                    </Form>
                )}
            </div>
        );
    }
;

export default EventConfig;
